import { css } from '@emotion/core';
import { Pagination, Select, Skeleton } from 'antd';
import { useRouter } from 'next/router';
import React, { useState } from 'react';

import { formatPage } from '@/src/forum/helper';
import Heading from '@/src/shared/components/Heading';
import { bp, colors, space } from '@/styles';
import formatURL from '@/utils/formatURL';

import ThreadListItem from './components/ThreadListItem';

const { m } = space;
const PAGE_SIZE = 10;

const style = {
  title: css`
    display: none;
    @media ${bp.md} {
      margin-bottom: 32px;
      display: block;
    }
  `,
  topContainer: css`
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
    @media ${bp.md} {
      margin-bottom: 24px;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  `,
  pageContainer: css`
    display: flex;
    flex-direction: column;
    > div,
    ul {
      margin-bottom: 12px;
    }
    @media ${bp.md} {
      flex-direction: row;
      align-items: center;
      > div,
      ul {
        margin-bottom: 0 !important;
      }
    }
  `,
  select: css`
    display: flex;
    align-items: center;
    > .ant-select {
      min-width: 17.8rem;
    }
  `,
  resultsCnt: css`
    font-size: 12px;
    line-height: 20px;
    color: ${colors.secondaryGray};
    font-weight: 900;
    margin-right: 12px;
  `,
};

const getSortFieldState = (sortField, sortOrder) => {
  const field = sortField && sortField.toLowerCase() === 'title' ? 'title' : 'updateDate';
  const order = sortOrder && ['asc', 'desc'].includes(sortOrder.toLowerCase()) ? sortOrder : 'desc';
  return `${field}-${order}`;
};

const ForumThreadList = props => {
  const { searching, threads, count } = props;
  const router = useRouter();
  const {
    query: {
      page,
      pageSize = PAGE_SIZE,
      sortField,
      sortOrder,
      searchText = '',
      states = '',
      topics = '',
    },
  } = router;

  const didSearch = searchText?.length > 0 || states?.length > 0 || topics?.length > 0;

  const [current, setCurrent] = useState(formatPage(page));
  const [sortFieldState, setSortFieldState] = useState(getSortFieldState(sortField, sortOrder));

  const handlePaginationChange = pageNum => {
    setCurrent(pageNum);
    const newURL = formatURL(router.asPath, { page: pageNum });
    router.replace(newURL, newURL, { shallow: true });
  };

  const handleSelectChange = sort => {
    setSortFieldState(sort);
    const [field, order] = sort.split('-');
    const newURL = formatURL(router.asPath, { sortField: field, sortOrder: order });
    router.replace(newURL, newURL, { shallow: true });
  };

  const renderThreadList = () => {
    if (searching) {
      return (
        <>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </>
      );
    }
    return threads.map(thread => (
      <ThreadListItem
        key={thread.ThreadId}
        {...thread}
        href={`/compliance/market-conduct/forum/${thread.ThreadId}`}
        as={`/compliance/market-conduct/forum/${thread.ThreadId}${didSearch ? '?search=true' : ''}`}
      />
    ));
  };

  const startNum = count ? (current - 1) * pageSize + 1 : 0;
  const endNum = count ? Math.min(count, current * pageSize) : 0;

  return (
    <div>
      <Heading type={2} weight="bolder" theme="primaryGray" css={style.title}>
        Forum Threads
      </Heading>
      <div css={style.topContainer}>
        <div css={style.pageContainer}>
          <div css={style.resultsCnt}>
            RESULTS {startNum}-{endNum} of {count}
          </div>
          <Pagination
            onChange={handlePaginationChange}
            total={count}
            pageSize={pageSize}
            current={current}
          />
        </div>
        <div css={style.select}>
          <Heading type={4} weight="bolder" theme="primaryGray" css={m.r12}>
            Sort by
          </Heading>
          <Select defaultValue={sortFieldState} onChange={handleSelectChange}>
            <Select.Option value="updateDate-asc">Date Published (Asc)</Select.Option>
            <Select.Option value="updateDate-desc">Date Published (Desc)</Select.Option>
            <Select.Option value="title-asc">Title (Asc)</Select.Option>
            <Select.Option value="title-desc">Title (Desc)</Select.Option>
          </Select>
        </div>
      </div>
      {renderThreadList()}
    </div>
  );
};

export default ForumThreadList;
